/* eslint-disable max-lines */
/**
 * SubtitleRow - Individual subtitle row with status, file/name, language, and action
 *
 * Displays either:
 * - An existing subtitle (from Stream) with dropdown actions (delete, view details)
 * - A new subtitle being uploaded with upload action
 * - An empty input row for adding new subtitles
 */

import type { ReactElement } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Circle,
  FileText,
  Languages,
  Loader2,
  MoreVertical,
  Trash2,
  Upload,
} from "lucide-react";
import { normalizeLanguageName } from "../../../utils/languageMapping";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../ui/dropdown-menu";
import { LanguageSingleSelect } from "./LanguageSingleSelect";

/** Subtitle metadata from API with validation info */
interface SubtitleMetadata {
  validation?: {
    valid: boolean;
    errors: string[];
    /** Detailed warnings (individual cue messages) */
    warnings: string[];
    /** Grouped warnings for compact tooltip display */
    warningsSummary?: string[];
  };
  processing?: {
    cueCount: number;
    sizeKB: number;
    wasConverted: boolean;
    wasNormalized: boolean;
    timecodeOffset: number;
  };
  [key: string]: unknown;
}

export interface ExistingSubtitle {
  id: string;
  lang: string;
  format: string;
  url: string;
  forced: boolean;
  metadata?: SubtitleMetadata | null;
  /** File name for display in details */
  fileName?: string;
}

export interface PendingSubtitle {
  fileName: string;
  language: string;
  /** Processing warnings from VTT parsing */
  warnings?: string[];
}

interface SubtitleRowProps {
  /** Existing subtitle from database/Stream */
  readonly existing?: ExistingSubtitle;
  /** Pending subtitle waiting to be uploaded */
  readonly pending?: PendingSubtitle;
  /** Empty row for new subtitle input */
  readonly isEmpty?: boolean;
  /** Currently processing/loading */
  readonly isLoading?: boolean;
  /** Callback when delete is clicked (existing subtitles) */
  readonly onDelete?: (subtitle: ExistingSubtitle) => void;
  /** Callback when view details is clicked (existing subtitles) */
  readonly onViewDetails?: (subtitle: ExistingSubtitle) => void;
  /** Callback when translate is clicked (existing subtitles) */
  readonly onTranslate?: (subtitle: ExistingSubtitle) => void;
  /** Callback when upload is clicked (pending subtitles) */
  readonly onUpload?: (subtitle: PendingSubtitle) => void;
  /** Callback when file is selected (empty rows) */
  readonly onFileSelect?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /** Callback when language changes (pending subtitles) */
  readonly onLanguageChange?: (language: string) => void;
  /** Unique ID for file input */
  readonly inputId?: string;
  /** Whether actions are disabled */
  readonly disabled?: boolean;
}

interface StatusIconProps {
  readonly existing?: ExistingSubtitle;
  readonly pending?: PendingSubtitle;
  readonly isEmpty?: boolean;
  readonly isLoading?: boolean;
}

/**
 * Status indicator icon based on subtitle state
 * Shows green for valid, yellow with tooltip for warnings
 */
function StatusIcon({ existing, pending, isEmpty, isLoading }: Readonly<StatusIconProps>): ReactElement {
  if (isLoading) {
    return <Loader2 className="h-4 w-4 text-neutral-400 animate-spin" />;
  }

  if (isEmpty) {
    return <Circle className="h-4 w-4 text-neutral-600" />;
  }

  if (existing) {
    // Existing subtitle - check metadata for validation warnings
    const validation = existing.metadata?.validation;
    const warnings = validation?.warnings ?? [];
    // Use warningsSummary for tooltip (compact display), fall back to detailed warnings
    const displayWarnings = validation?.warningsSummary ?? warnings;
    const hasWarnings = warnings.length > 0;

    return (
      <div className="relative group">
        <CheckCircle2 className={`h-4 w-4 ${hasWarnings ? "text-yellow-500" : "text-green-500"}`} />
        {hasWarnings && (
          <div
            className="absolute left-0 bottom-full mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-100 z-[100] pointer-events-none"
            style={{ width: "max-content", maxWidth: "320px" }}
          >
            <div className="bg-neutral-900 border border-yellow-700/30 rounded px-3 py-2 text-xs text-yellow-300 shadow-lg flex items-start gap-1.5">
              <AlertTriangle className="h-3 w-3 flex-shrink-0 mt-0.5" />
              <span>{displayWarnings.join("; ")}</span>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (pending) {
    // Pending subtitle - yellow if warnings, green if clean
    const warnings = pending.warnings;
    const hasWarnings = warnings && warnings.length > 0;
    return (
      <div className="relative group">
        <CheckCircle2 className={`h-4 w-4 ${hasWarnings ? "text-yellow-500" : "text-green-500"}`} />
        {hasWarnings && (
          <div
            className="absolute left-0 bottom-full mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-100 z-[100] pointer-events-none"
            style={{ width: "max-content", maxWidth: "320px" }}
          >
            <div className="bg-neutral-900 border border-yellow-700/30 rounded px-3 py-2 text-xs text-yellow-300 shadow-lg flex items-start gap-1.5">
              <AlertTriangle className="h-3 w-3 flex-shrink-0 mt-0.5" />
              <span>{warnings.join("; ")}</span>
            </div>
          </div>
        )}
      </div>
    );
  }

  return <Circle className="h-4 w-4 text-neutral-600" />;
}

/**
 * Truncate filename for display
 */
function truncateFileName(fileName: string, maxLength = 24): string {
  if (fileName.length <= maxLength) return fileName;
  const ext = fileName.split(".").pop() ?? "";
  const name = fileName.slice(0, fileName.length - ext.length - 1);
  const truncatedName = name.slice(0, maxLength - ext.length - 4) + "...";
  return `${truncatedName}.${ext}`;
}

/**
 * Empty input row for selecting a new subtitle file
 */
function EmptyRow({
  inputId,
  disabled,
  onFileSelect,
}: Readonly<{
  inputId: string;
  disabled: boolean;
  onFileSelect?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}>): ReactElement {
  return (
    <div className="flex items-center gap-3 py-2">
      <StatusIcon isEmpty />

      {/* File select button */}
      <input
        id={inputId}
        type="file"
        accept=".vtt,.srt"
        onChange={onFileSelect}
        className="hidden"
        disabled={disabled}
      />
      <button
        type="button"
        onClick={() => document.getElementById(inputId)?.click()}
        disabled={disabled}
        className="flex-1 text-left px-3 py-2 text-sm text-neutral-400 bg-neutral-900/50 border border-neutral-700 rounded hover:border-neutral-600 hover:text-neutral-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Choose subtitle file...
      </button>

      {/* Language select (disabled for empty row) */}
      <div className="w-32">
        <LanguageSingleSelect value="" onChange={() => {}} disabled />
      </div>

      {/* Placeholder for action button */}
      <div className="w-8 h-8" />
    </div>
  );
}

/**
 * Row displaying an existing subtitle with dropdown actions
 */
function ExistingRow({
  existing,
  isLoading,
  disabled,
  onDelete,
  onViewDetails,
  onTranslate,
}: Readonly<{
  existing: ExistingSubtitle;
  isLoading: boolean;
  disabled: boolean;
  onDelete?: (subtitle: ExistingSubtitle) => void;
  onViewDetails?: (subtitle: ExistingSubtitle) => void;
  onTranslate?: (subtitle: ExistingSubtitle) => void;
}>): ReactElement {
  const displayName = normalizeLanguageName(existing.lang);
  const hasMetadata = existing.metadata?.validation || existing.metadata?.processing;

  return (
    <div className="flex items-center gap-3 py-2">
      <StatusIcon existing={existing} isLoading={isLoading} />

      {/* Display name/language */}
      <div className="flex-1 px-3 py-2 text-sm text-white bg-neutral-900/50 border border-neutral-700 rounded">
        <span className="font-medium">{displayName}</span>
        <span className="text-neutral-500 ml-2">({existing.format})</span>
        {existing.forced && <span className="text-lumi-accent ml-2">Forced</span>}
      </div>

      {/* Language display (read-only) */}
      <div className="w-32 px-3 py-2 text-sm text-neutral-400 bg-neutral-900/30 border border-neutral-800 rounded">
        {existing.lang.toUpperCase()}
      </div>

      {/* Actions dropdown menu */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            disabled={disabled || isLoading}
            className="w-8 h-8 flex items-center justify-center text-neutral-400 hover:text-white
                            hover:bg-neutral-800 rounded transition-colors disabled:opacity-50"
          >
            <MoreVertical className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {hasMetadata && (
            <DropdownMenuItem onClick={() => onViewDetails?.(existing)}>
              <FileText className="h-4 w-4 mr-2" />
              View Details
            </DropdownMenuItem>
          )}
          {onTranslate && (
            <DropdownMenuItem onClick={() => onTranslate(existing)}>
              <Languages className="h-4 w-4 mr-2" />
              Translate...
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={() => onDelete?.(existing)} className="text-red-400 focus:text-red-300">
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

/**
 * Row displaying a pending subtitle ready to upload
 */
function PendingRow({
  pending,
  isLoading,
  disabled,
  onUpload,
  onLanguageChange,
}: Readonly<{
  pending: PendingSubtitle;
  isLoading: boolean;
  disabled: boolean;
  onUpload?: (subtitle: PendingSubtitle) => void;
  onLanguageChange?: (language: string) => void;
}>): ReactElement {
  return (
    <div className="flex items-center gap-3 py-2">
      <StatusIcon pending={pending} isLoading={isLoading} />

      {/* Truncated file name */}
      <div className="flex-1 px-3 py-2 text-sm text-white bg-neutral-900/50 border border-neutral-700 rounded truncate">
        {truncateFileName(pending.fileName)}
      </div>

      {/* Language select */}
      <div className="w-32">
        <LanguageSingleSelect
          value={pending.language}
          onChange={(lang: string) => onLanguageChange?.(lang)}
          disabled={disabled || isLoading}
        />
      </div>

      {/* Upload button */}
      <button
        type="button"
        onClick={() => onUpload?.(pending)}
        disabled={disabled || isLoading}
        className="w-8 h-8 flex items-center justify-center text-lumi-accent hover:text-lumi-accent/80 hover:bg-amber-900/20 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        title="Upload subtitle"
      >
        <Upload className="h-4 w-4" />
      </button>
    </div>
  );
}

export function SubtitleRow({
  existing,
  pending,
  isEmpty = false,
  isLoading = false,
  onDelete,
  onViewDetails,
  onTranslate,
  onUpload,
  onFileSelect,
  onLanguageChange,
  inputId = "subtitle-file",
  disabled = false,
}: Readonly<SubtitleRowProps>): ReactElement {
  if (isEmpty) {
    return <EmptyRow inputId={inputId} disabled={disabled} onFileSelect={onFileSelect} />;
  }

  if (existing) {
    return (
      <ExistingRow
        existing={existing}
        isLoading={isLoading}
        disabled={disabled}
        onDelete={onDelete}
        onViewDetails={onViewDetails}
        onTranslate={onTranslate}
      />
    );
  }

  if (pending) {
    return (
      <PendingRow
        pending={pending}
        isLoading={isLoading}
        disabled={disabled}
        onUpload={onUpload}
        onLanguageChange={onLanguageChange}
      />
    );
  }

  // Fallback empty state
  return <div className="text-sm text-neutral-500 italic py-2">No subtitle data</div>;
}
